import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Minus, Plus, User } from "lucide-react";
import { useI18n } from "@/hooks/useI18n";

// Types
export interface Guest {
  id: number;
  age: number;
}

interface GuestManagerProps {
  totalGuests: number;
  guests: Guest[];
  onAddGuest: () => void;
  onRemoveGuest: () => void;
  onUpdateGuestAge: (guestId: number, newAge: number) => void;
  disabled?: boolean;
  /**
   * Il numero di viaggiatori arriva da un altro campo: qui si mostra soltanto,
   * i pulsanti +/- spariscono ma le eta' restano modificabili.
   */
  readOnlyCount?: boolean;
  /**
   * Taglia dei form in modale: contatore in linea alto 36px e riquadri eta' piu'
   * piccoli, al posto dei bottoni tondi da 48px pensati per il form pubblico.
   */
  compact?: boolean;
}

const CounterButton = ({
  onClick,
  children,
  disabled = false
}: {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) => (
  <Button
    type="button"
    variant="outline"
    size="sm"
    className="w-12 h-12 rounded-full bg-gray-50 border-gray-200"
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </Button>
);

export const GuestManager: React.FC<GuestManagerProps> = ({
  totalGuests,
  guests,
  onAddGuest,
  onRemoveGuest,
  onUpdateGuestAge,
  disabled = false,
  readOnlyCount = false,
  compact = false
}) => {
  const { t } = useI18n();
  const showCounter = !disabled && !readOnlyCount;
  return (
    <div className={compact ? 'space-y-3' : 'space-y-6'}>
      {compact ? (
        // Stesso stepper del modale richiesta cliente: una cella riquadrata alta 36px.
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-700">{t('travelRequest.numberOfTravelers')}</span>
          <div className="flex h-9 w-28 items-center justify-between rounded-md border border-input">
            <button
              type="button"
              onClick={onRemoveGuest}
              disabled={disabled || !showCounter || totalGuests <= 1}
              className="flex h-full w-9 items-center justify-center text-gray-500 hover:text-gray-900 disabled:opacity-30"
              aria-label={String(t('common.remove'))}
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span className="text-sm font-medium tabular-nums">{totalGuests}</span>
            <button
              type="button"
              onClick={onAddGuest}
              disabled={disabled || !showCounter || totalGuests >= 16}
              className="flex h-full w-9 items-center justify-center text-gray-500 hover:text-gray-900 disabled:opacity-30"
              aria-label={String(t('common.add'))}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : (
      <div className="flex items-center justify-between">
        <span>{t('travelRequest.numberOfTravelers')}</span>
        <div className="flex items-center space-x-4">
          {showCounter && (
            <CounterButton onClick={onRemoveGuest} disabled={disabled}>
              <Minus className="w-5 h-5" />
            </CounterButton>
          )}
          <h4 className="w-8 text-center">
            {totalGuests}
          </h4>
          {showCounter && (
            <CounterButton onClick={onAddGuest} disabled={totalGuests >= 16}>
              <Plus className="w-5 h-5" />
            </CounterButton>
          )}
        </div>
      </div>
      )}

      {guests.length > 0 && (
        <div className={`flex flex-wrap ${compact ? 'gap-2' : 'gap-3'}`}>
          {guests.map((guest, index) => (
            <div
              key={guest.id}
              className={`flex items-center gap-2 border border-gray-200 bg-gray-50 ${
                compact ? 'rounded-lg px-2 py-1' : 'rounded-xl px-3 py-2'
              }`}
            >
              <User className={`text-gray-400 flex-shrink-0 ${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
              <Input
                type="number"
                min="0"
                max="120"
                value={guest.age}
                onChange={(e) => {
                  const newAge = parseInt(e.target.value) || 0;
                  onUpdateGuestAge(guest.id, Math.max(0, newAge));
                }}
                className={`text-center rounded-lg px-1 ${compact ? 'w-12 h-7 text-sm' : 'w-14 h-8'}`}
                disabled={disabled}
              />
              <span className="text-xs text-gray-400 whitespace-nowrap">{t('travelRequest.years')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
